import { QueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import { SnackbarContextType } from '../contexes/SnackBarProvider';
import { AnalyticsEvents } from '../enums/AnalyticsEvents';
import {
  Agent,
  AgentFormData,
  AgentStatus,
  Appointment,
  DepartmentContact,
} from '../interfaces/Agent';
import { ResponseObject } from '../interfaces/App';
import Company from '../interfaces/Company';
import Knowledge, { KnowledgeFileData } from '../interfaces/Knowledge';
import User from '../interfaces/User';
import { createAgent, uploadEmployeeDirectory } from '../services/agentService';
import { updateCalendarSecret } from '../services/calendarService';
import { deleteKnowledge } from '../services/knowledgeService';
import { TranslationKey } from '../types/i18n';
import { toAccessPayload } from '../utils/resourceAccess';
import {
  buildTransferCallsCapability,
  getTransferCallsArray,
  removeWhiteSpaces,
  setAgentsImageFile,
} from './agentHelper';
import {
  createNewKnowledge,
  getOrCreateKnowledgeId,
  saveWebscrapConfig,
  uploadKnowledgeFiles,
} from './knowledgeHelper';

export interface CreateAgentSubmitContext {
  methods: UseFormReturn<AgentFormData>;
  /** MixVoIP iframe: SIP/extension is assigned in the cockpit. */
  skipPhoneLineRequirement?: boolean;
  isBookingCapabilityEnabled: boolean;
  currentUser: User | null;
  companyDetails?: Company;
  knowledgeFilesData: KnowledgeFileData[];
  executeStagedDeletions: () => Promise<{ failed: string[] }>;
  clearKnowledgeFilesData: () => void;
  queryClient: QueryClient;
  track: (event: string, props?: Record<string, unknown>) => void;
  displayMessage: SnackbarContextType;
  t: (key: TranslationKey, options?: Record<string, unknown>) => string;
  agentsCount: number;
  setIsProcessing: (value: boolean) => void;
  setIsKnowledgeCreated: (value: boolean) => void;
  setIsAgentCreated: (value: boolean) => void;
  setIsFilesUploaded: (value: boolean) => void;
  setAgentsCount: (value: number) => void;
  setNewAgent: (agent: Agent) => void;
}

// Handles the CREATE-workflow branch of AgentForm's submit: restores any
// booking-capability fields missing from `data` (a session-storage OAuth
// recovery safety net — see useAgentFormSessionRecovery), creates the
// knowledge base and agent, uploads the staged employee directory file and
// knowledge files, updates the calendar secret, and tracks analytics —
// rolling back a freshly-created knowledge base if agent creation fails.
// See docs/specs/agent-form-click-to-jump-navigation.md.
export const submitCreateAgent = async (
  data: AgentFormData,
  ctx: CreateAgentSubmitContext
): Promise<void> => {
  const {
    methods,
    skipPhoneLineRequirement,
    isBookingCapabilityEnabled,
    currentUser,
    companyDetails,
    knowledgeFilesData,
    executeStagedDeletions,
    clearKnowledgeFilesData,
    queryClient,
    track,
    displayMessage,
    t,
    agentsCount,
    setIsProcessing,
    setIsKnowledgeCreated,
    setIsAgentCreated,
    setIsFilesUploaded,
    setAgentsCount,
    setNewAgent,
  } = ctx;

  let newKnowledgeBaseId: string | undefined;

  // Restore any missing form data from session storage before submission (only if booking capability is enabled)
  if (isBookingCapabilityEnabled) {
    const savedState = sessionStorage.getItem('elora_agent_form_state');
    if (savedState) {
      try {
        const parsedData = JSON.parse(savedState);
        // Ensure knowledge name is populated from session storage if not in current form data
        if (!data.knowledge_name && parsedData.knowledge_name) {
          data.knowledge_name = parsedData.knowledge_name;
        }
        if (!data.knowledge_language && parsedData.knowledge_language) {
          data.knowledge_language = parsedData.knowledge_language;
        }
        if (!data.knowledge_mode && parsedData.knowledge_mode) {
          data.knowledge_mode = parsedData.knowledge_mode;
        }
        // Also restore agent behavior data if missing
        if (!data.agent_name && parsedData.agent_name) {
          data.agent_name = parsedData.agent_name;
        }
        if (!data.role_id && parsedData.role_id) {
          data.role_id = parsedData.role_id;
        }
        if (!data.voice && parsedData.voice) {
          data.voice = parsedData.voice;
        }
        if (!data.default_sentence && parsedData.default_sentence) {
          data.default_sentence = parsedData.default_sentence;
        }
        if (!data.data_to_collect && parsedData.data_to_collect) {
          data.data_to_collect = parsedData.data_to_collect;
        }
        if (!data.default_language && parsedData.default_language) {
          data.default_language = parsedData.default_language;
        }
        // Also restore capabilities data if missing
        if (!data.external_capabilities && parsedData.external_capabilities) {
          data.external_capabilities = parsedData.external_capabilities;
        }
        if (!data.transfer_calls && parsedData.transfer_calls) {
          data.transfer_calls = parsedData.transfer_calls;
        }
        if (!data.insert_appointment && parsedData.insert_appointment) {
          data.insert_appointment = parsedData.insert_appointment;
        }
        if (
          !data.employee_directory_staged_file &&
          parsedData.employee_directory_staged_file
        ) {
          data.employee_directory_staged_file =
            parsedData.employee_directory_staged_file;
        }

        // Also update the form values in react-hook-form to ensure consistency
        if (parsedData.knowledge_name) {
          methods.setValue('knowledge_name', parsedData.knowledge_name);
        }
        if (parsedData.knowledge_language) {
          methods.setValue('knowledge_language', parsedData.knowledge_language);
        }
        if (parsedData.knowledge_mode) {
          methods.setValue('knowledge_mode', parsedData.knowledge_mode);
        }
        if (parsedData.agent_name) {
          methods.setValue('agent_name', parsedData.agent_name);
        }
        if (parsedData.role_id) {
          methods.setValue('role_id', parsedData.role_id);
        }
        if (parsedData.voice) {
          methods.setValue('voice', parsedData.voice);
        }
        if (parsedData.default_sentence) {
          methods.setValue('default_sentence', parsedData.default_sentence);
        }
        if (parsedData.data_to_collect) {
          methods.setValue('data_to_collect', parsedData.data_to_collect);
        }
        if (parsedData.default_language) {
          methods.setValue('default_language', parsedData.default_language);
        }
        if (parsedData.external_capabilities) {
          methods.setValue(
            'external_capabilities',
            parsedData.external_capabilities
          );
        }
        if (parsedData.transfer_calls) {
          methods.setValue('transfer_calls', parsedData.transfer_calls);
        }
        if (parsedData.insert_appointment) {
          methods.setValue('insert_appointment', parsedData.insert_appointment);
        }
        if (parsedData.employee_directory_staged_file) {
          methods.setValue(
            'employee_directory_staged_file',
            parsedData.employee_directory_staged_file
          );
        }
      } catch (error) {
        console.warn(
          'Failed to restore form data from session storage:',
          error
        );
      }
    }
  }

  try {
    //
    // Reuse an existing knowledge base, create a new one, or — when the
    // user opted out — go without (the agent then answers from its role
    // prompt alone; the voice runtime skips RAG for an empty knowledge_ids).
    //
    let knowledgeId: string | undefined;
    if (data.knowledge_mode !== 'none') {
      knowledgeId = await getOrCreateKnowledgeId(
        data.existing_knowledge_id,
        () =>
          createNewKnowledge(
            data.knowledge_name,
            currentUser!.id,
            currentUser!.company_id,
            data.knowledge_language
          )
      );
      // Only a freshly-created knowledge base should be rolled back on
      // failure below — a reused one is a shared resource we don't own.
      if (!data.existing_knowledge_id) {
        newKnowledgeBaseId = knowledgeId;
      }
    }
    setIsKnowledgeCreated(true);

    // The create endpoint doesn't accept webscrap config directly (it
    // needs a knowledge id to attach to), so persist it with a
    // follow-up update once the knowledge exists. Non-fatal: the warning
    // is reported with the rest once the agent is created. Skipped if this
    // is a reused existing knowledge base, or if the user opted out of
    // having one.
    let webscrapWarning: string | undefined;
    if (knowledgeId && !data.existing_knowledge_id && data.webscrap_enabled) {
      webscrapWarning = await saveWebscrapConfig(knowledgeId, data, t);
    }

    //
    // Create a new agent
    //
    const agentData: Agent = {
      avatar: setAgentsImageFile(),
      company: companyDetails?.name ?? '',
      company_id: currentUser!.company_id,
      default_language: data.default_language,
      data_to_collect: data.data_to_collect,
      default_sentence: data.default_sentence,
      analytics_language: data.analytics_language,
      call_categories: data.call_categories,
      auto_fix: data.auto_fix,
      external_capabilities: {
        ...buildTransferCallsCapability(data.transfer_calls),
        ...(data.insert_appointment && data.insert_appointment.length > 0
          ? {
              insert_appointment: data.insert_appointment.map(
                (item: Appointment) => ({
                  start_time: item.start_time,
                  end_time: item.end_time,
                  min_advance_notice_hours: item.min_advance_notice_hours,
                  appointment_length_minutes: item.appointment_length_minutes,
                  app: item.app,
                })
              ),
            }
          : {}),
      },
      knowledge_ids: knowledgeId ? [knowledgeId] : [],
      name: data.agent_name,
      opening_sentence: data.opening_sentence,
      role_id: data.role_id,
      status: AgentStatus.OFFLINE,
      ...(skipPhoneLineRequirement
        ? {}
        : {
            system_phone_number_country_code:
              data.system_phone_number_country_code,
          }),
      user_id: currentUser!.id,
      voice: data.voice,
      access: toAccessPayload(data.access),
    };

    // Remove whitespaces on phone numbers
    if (data.system_phone_number) {
      agentData.system_phone_number = removeWhiteSpaces(
        data.system_phone_number
      );
    }
    if (data.custom_phone_number) {
      agentData.custom_phone_number = removeWhiteSpaces(
        data.custom_phone_number
      );
    }
    if (data.failure_backup_phone_number) {
      agentData.failure_backup_phone_number = removeWhiteSpaces(
        data.failure_backup_phone_number
      );
    }
    if (data.feedback_sampling_rate !== undefined) {
      agentData.feedback_sampling_rate = data.feedback_sampling_rate / 100;
    }
    if (data.time_zone) {
      agentData.time_zone = data.time_zone;
    }
    if (data.working_hours) {
      agentData.working_hours = data.working_hours;
    }
    if (data.holidays && data.holidays.length > 0) {
      agentData.holidays = data.holidays;
    }

    const agent: ResponseObject = await createAgent(agentData);

    // The backend silently prunes past one-off holiday dates on every
    // save — re-sync from its response rather than trusting what was
    // just submitted, so pruning is reflected immediately instead of
    // surfacing as a surprise the next time this agent is loaded.
    if (agent?.message?.holidays !== undefined) {
      methods.setValue('holidays', agent.message.holidays);
    }

    // Upload the staged employee directory file now that we have a
    // real agent id — it couldn't be uploaded earlier (see
    // EmployeeDirectoryUploadModal's deferUpload mode). Wrapped in its
    // own try/catch (unlike the calendar secret call below) because
    // uploadEmployeeDirectory does not swallow its own errors, and a
    // failed upload must not block the already-successful agent
    // creation or roll it back.
    if (
      data.transfer_calls === 'employee_directory' &&
      data.employee_directory_staged_file &&
      agent?.message?.id
    ) {
      try {
        await uploadEmployeeDirectory(
          agent.message.id,
          data.employee_directory_staged_file.base64
        );
      } catch {
        displayMessage(
          t(
            "Agent created, but the employee directory failed to upload. You can upload it again from the agent's capabilities settings."
          ),
          'error'
        );
      }
    } else if (
      data.transfer_calls === 'employee_directory' &&
      !data.employee_directory_staged_file &&
      agent?.message?.id
    ) {
      // The mode was selected but no file ever made it back into form
      // state (e.g. it was lost across a full page reload). Surface
      // this instead of silently creating an empty employee directory.
      displayMessage(
        t(
          "Agent created, but no employee directory file was found to upload. Please upload it from the agent's capabilities settings."
        ),
        'warning'
      );
    }

    // Update the (placeholder) calendar client secret
    // We need to do this because the agent ID is created later in the flow.
    if (
      isBookingCapabilityEnabled &&
      agent?.message?.external_capabilities?.insert_appointment
    ) {
      const placeholderAgentId =
        sessionStorage.getItem('placeholderAgentId') ?? '';
      await updateCalendarSecret(placeholderAgentId, {
        app: agent?.message?.external_capabilities?.insert_appointment[0]?.app,
        agent_id: agent?.message?.id,
      });
    }

    // --- Analytics: Track agent line choice and assignment success ---
    track(AnalyticsEvents.AGENT_LINE_CHOSEN, {
      agentId: agent?.message?.id || 'new',
      lineType: data.custom_phone_number ? 'custom' : 'system',
      phoneNumber: data.custom_phone_number || data.system_phone_number,
      countryCode: data.system_phone_number_country_code,
    });
    if (agent.status === 'success') {
      track(AnalyticsEvents.AGENT_LINE_ASSIGNMENT_SUCCESS, {
        agentId: agent?.message?.id || 'new',
        lineType: data.custom_phone_number ? 'custom' : 'system',
        phoneNumber: data.custom_phone_number || data.system_phone_number,
        countryCode: data.system_phone_number_country_code,
      });
      // --- Analytics: Track capability config success ---
      track(AnalyticsEvents.AGENT_CAPABILITY_CONFIG_SUCCESS, {
        agentId: agent?.message?.id || 'new',
        capabilities: Object.keys(agentData.external_capabilities || {}),
        transferCallCount: getTransferCallsArray(data.transfer_calls).length,
        departments: getTransferCallsArray(data.transfer_calls).map(
          (tc: DepartmentContact) => tc.department_name
        ),
      });
      setIsAgentCreated(true);
      setAgentsCount(agentsCount + 1);
      setNewAgent({ ...agentData, id: agent.message.id });
    } else {
      track(AnalyticsEvents.AGENT_LINE_ASSIGNMENT_FAILURE, {
        error: agent?.message,
        lineType: data.custom_phone_number ? 'custom' : 'system',
        phoneNumber: data.custom_phone_number || data.system_phone_number,
        countryCode: data.system_phone_number_country_code,
      });
      // --- Analytics: Track capability config failure ---
      track(AnalyticsEvents.AGENT_CAPABILITY_CONFIG_FAILURE, {
        error: agent?.message,
        capabilities: Object.keys(agentData.external_capabilities || {}),
        transferCallCount: getTransferCallsArray(data.transfer_calls).length,
        departments: getTransferCallsArray(data.transfer_calls).map(
          (tc: DepartmentContact) => tc.department_name
        ),
      });
    }

    // Upload the knowledge files (these came from FilePond) and flush
    // any staged deletions in parallel, mirroring KnowledgeForm's
    // submit wiring. Settled (not `all`) so a failed upload can't
    // suppress the report of deletions that already completed.
    const [uploadResult, deletionResult] = await Promise.allSettled([
      knowledgeId
        ? uploadKnowledgeFiles(knowledgeFilesData, {
            id: knowledgeId,
          } as Knowledge)
        : Promise.resolve(),
      executeStagedDeletions(),
    ]);
    setIsFilesUploaded(true);

    // The snackbar only holds one message at a time, so warnings are
    // combined instead of clobbering each other. Each part is a complete,
    // independently-translated sentence.
    const parts: string[] = webscrapWarning ? [webscrapWarning] : [];
    if (deletionResult.status === 'fulfilled') {
      if (deletionResult.value.failed.length > 0) {
        parts.push(
          t('Could not delete: {{files}}', {
            files: deletionResult.value.failed.join(', '),
          })
        );
      }
    } else {
      parts.push(
        deletionResult.reason instanceof Error
          ? deletionResult.reason.message
          : t('Some staged file deletions could not be completed.')
      );
    }
    if (parts.length > 0) {
      displayMessage(parts.join(' '), 'warning', 6000);
    }

    if (uploadResult.status === 'rejected') {
      throw uploadResult.reason;
    }

    setIsAgentCreated(true);
    setNewAgent({ ...agent?.message });
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
      displayMessage(err.message, 'error', 8000);
      setIsProcessing(false);

      // --- Analytics: Track assignment failure ---
      track(AnalyticsEvents.AGENT_LINE_ASSIGNMENT_FAILURE, {
        error: err.message,
        lineType: data.custom_phone_number ? 'custom' : 'system',
        phoneNumber: data.custom_phone_number || data.system_phone_number,
        countryCode: data.system_phone_number_country_code,
      });
      // --- Analytics: Track capability config failure ---
      track(AnalyticsEvents.AGENT_CAPABILITY_CONFIG_FAILURE, {
        error: err.message,
        capabilities:
          getTransferCallsArray(data.transfer_calls).length > 0
            ? ['transfer_calls']
            : [],
        transferCallCount: getTransferCallsArray(data.transfer_calls).length,
        departments: getTransferCallsArray(data.transfer_calls).map(
          (tc: DepartmentContact) => tc.department_name
        ),
      });

      // Agent creation clean-up
      if (newKnowledgeBaseId) {
        await deleteKnowledge(newKnowledgeBaseId);
        // Clear React Query cache for knowledge
        queryClient.removeQueries({ queryKey: ['knowledges'] });
        queryClient.removeQueries({
          queryKey: ['knowledges-with-attachment'],
        });
      }
    }
  } finally {
    clearKnowledgeFilesData();
  }
};
